// Each action's button label, and what it reads while the action runs.
const ACTION_LABELS: Record<string, { label: string; busyLabel: string }> = {
  auth: { label: 'Test Connection', busyLabel: 'Testing…' },
  fetch_samples: { label: 'Fetch Data', busyLabel: 'Fetching…' },
  show_permissions: { label: 'Request Information', busyLabel: 'Requesting…' },
  reset_cached_vessel_data: { label: 'Clear State', busyLabel: 'Clearing…' },
  view_cached_vessel_data: { label: 'Get State', busyLabel: 'Getting…' },
};
const DEFAULT_ACTION_LABELS = { label: 'Test Action', busyLabel: 'Running…' };

export function getButtonText(
  value: string,
  t: (key: string) => string,
): string {
  return t((ACTION_LABELS[value] ?? DEFAULT_ACTION_LABELS).label);
}

/** The button's label while its action runs, so the button itself shows the
 *  request is in progress. */
export function getBusyButtonText(
  value: string,
  t: (key: string) => string,
): string {
  return t((ACTION_LABELS[value] ?? DEFAULT_ACTION_LABELS).busyLabel);
}

/** A runner error arrives either as a string or as a dict ({action_id, error})
 *  from cdip's execute routes. Coerce so nothing renders "[object Object]". */
export function getResponseErrorMessage(
  testActionResponse: any,
): string | undefined {
  const raw = testActionResponse?.error ?? testActionResponse?.detail;
  if (typeof raw === 'string') return raw || undefined;
  if (raw && typeof raw === 'object' && 'error' in raw)
    return String(raw.error) || undefined;
  return undefined;
}

export function getResponseText(
  value: string,
  testActionResponse: any,
  t: (key: string) => string,
): any[] {
  // Credentials answer one question, so a transport failure reads the same as a
  // rejection. Keeps this wording identical to the draft flow, which has no
  // "Action Failed" state; the cause stays visible under View Details.
  if (value === 'auth') {
    const ok =
      !testActionResponse?.error && !!testActionResponse?.valid_credentials;
    return ok
      ? [t('Valid Credentials'), true]
      : [t('Invalid Credentials'), false];
  }
  if (testActionResponse?.error) {
    return [t('Action Failed'), false];
  }
  switch (value) {
    case 'fetch_samples':
      return testActionResponse.observations.length > 0
        ? [t('Fetch Successful'), true]
        : [t('Fetch Unsuccessful'), false];
    case 'show_permissions':
      return testActionResponse.data
        ? [t('Fetch Successful'), true]
        : [t('Fetch Unsuccessful'), false];
    default:
      // Any action may answer the credentials question, whatever its name.
      if (typeof testActionResponse?.valid_credentials === 'boolean') {
        return testActionResponse.valid_credentials
          ? [t('Valid Credentials'), true]
          : [t('Invalid Credentials'), false];
      }
      return [t('Response Received'), null];
  }
}

/** The account an `auth` check ran as, when the runner reports one. Only
 *  some runners send `connected_as`, so anything else reads as absent. */
export function getConnectedAs(testActionResponse: any): string | undefined {
  const raw = testActionResponse?.connected_as;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
}
